import React, { useState, useRef, useEffect } from 'react';
import './DragDropFileUpload.css';
import { isSetupSuccessful, isWorkerInitialized, ensureWorkerIsAvailable, pdfjsLib, getMaxPdfSize } from '../setupPdfWorker';

// Log PDF.js version for debugging
console.log('PDF.js version in DragDropFileUpload:', pdfjsLib.version);
console.log('PDF.js worker setup successful in component:', isSetupSuccessful);
console.log('PDF.js worker source:', pdfjsLib.GlobalWorkerOptions?.workerSrc || 'Not set');

// Maximum file sizes in bytes
const MAX_PDF_SIZE = getMaxPdfSize(); // From setupPdfWorker
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB for images

// Worker will be verified each time to ensure it's available
const checkWorkerStatus = () => {
  const initialized = isWorkerInitialized();
  console.log('PDF.js worker initialized status:', initialized);
  return initialized;
};

// Function to format file size for display
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes.toLocaleString() + ' bytes';
  else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1).toLocaleString() + ' KB';
  else return (bytes / (1024 * 1024)).toFixed(1).toLocaleString() + ' MB';
};

/**
 * Process PDF file and extract first page
 * @param {File} file - The PDF file to process
 * @param {Function} onFileSelect - Callback function to call with the processed file
 * @param {Function} clearFileInput - Function to clear the file input
 */
export const processPdfFile = async (file, onFileSelect, clearFileInput) => {
  console.log('Starting PDF processing for file:', file.name, 'Size:', formatFileSize(file.size));
  
  // Verify PDF.js worker initialization
  console.log('Verifying PDF.js worker...');
  
  // Check worker once more for good measure
  const workerAvailable = ensureWorkerIsAvailable();
  if (!workerAvailable) {
    console.error('PDF.js worker is not available');
    alert('PDF processing is not available at the moment. Please try again later or use an image file instead.');
    clearFileInput();
    return;
  }
  
  console.log('PDF.js worker confirmed available, proceeding with processing');
  
  try {
    // Load the PDF file
    console.log('Reading PDF file as ArrayBuffer');
    const fileReader = new FileReader();
    
    return new Promise((resolve, reject) => {
      fileReader.onload = async function() {
        try {
          console.log('FileReader loaded successfully, file size:', formatFileSize(this.result.byteLength));
          const typedArray = new Uint8Array(this.result);
          
          try {
            // Load the PDF document with additional options to prevent CORS issues
            console.log('Creating PDF.js document loading task');
            const loadingTask = pdfjsLib.getDocument({
              data: typedArray,
              // Skip CMAP loading for now to reduce errors
              // If we need CMAP support for non-Latin fonts, we can add it back later
            });
            
            console.log('Waiting for PDF document to load');
            const pdf = await loadingTask.promise;
            console.log('PDF loaded successfully. Number of pages:', pdf.numPages);
            
            if (pdf.numPages === 0) {
              throw new Error('PDF document has no pages');
            }
            
            // Get the first page
            console.log('Getting first page');
            const page = await pdf.getPage(1);
            console.log('First page loaded successfully');
            
            // Calculate optimal scale based on page size to ensure quality
            // We want to aim for a reasonable pixel size (about 2000-3000 pixels for largest dimension)
            const originalViewport = page.getViewport({ scale: 1.0 });
            const maxDimension = Math.max(originalViewport.width, originalViewport.height);
            const targetDimension = 2500; // Target 2500px for largest dimension for good quality/file size balance
            const optimalScale = targetDimension / maxDimension;
            
            console.log('Original page dimensions:', originalViewport.width, 'x', originalViewport.height);
            console.log('Using optimal scale factor:', optimalScale.toFixed(2));
            
            // Set scale for rendering 
            console.log('Creating viewport with optimal scale');
            const viewport = page.getViewport({ scale: optimalScale });
            console.log('Viewport created with dimensions:', viewport.width, 'x', viewport.height);
            
            // Prepare canvas for rendering
            console.log('Creating canvas for rendering');
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d', { alpha: false }); // Disable alpha for better performance
            if (!context) {
              console.error('Failed to get canvas context');
              reject(new Error('Failed to get canvas context'));
              return;
            }
            
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            console.log('Canvas created with dimensions:', canvas.width, 'x', canvas.height);
            
            // Set white background to ensure no transparency issues
            context.fillStyle = 'white';
            context.fillRect(0, 0, canvas.width, canvas.height);
            
            // Render PDF page to canvas with higher quality settings
            console.log('Setting up render context');
            const renderContext = {
              canvasContext: context,
              viewport: viewport,
              // Optional settings for better rendering quality
              renderInteractiveForms: false,
              enableWebGL: true
            };
            
            console.log('Starting page rendering');
            const renderTask = page.render(renderContext);
            console.log('Waiting for render to complete');
            await renderTask.promise;
            console.log('Page rendered successfully');
            
            // Convert canvas to image data URL with quality options
            console.log('Converting canvas to image data URL');
            const imageDataUrl = canvas.toDataURL('image/png', 0.95); // 0.95 quality for PNG (though it's mostly lossless)
            console.log('Canvas converted to data URL, length:', imageDataUrl.length);
            
            // Create a File object from the image data
            console.log('Creating blob from data URL');
            try {
              const response = await fetch(imageDataUrl);
              console.log('Fetch response status:', response.status);
              const imageBlob = await response.blob();
              console.log('Blob created, size:', formatFileSize(imageBlob.size));
              
              // Use a clean filename with no special characters
              const cleanFileName = file.name.replace('.pdf', '').replace(/[^a-zA-Z0-9_-]/g, '_');
              const imageFile = new File(
                [imageBlob], 
                `${cleanFileName}-page1.png`, 
                { type: 'image/png' }
              );
              console.log('File object created:', imageFile.name, 'Size:', formatFileSize(imageFile.size));
              
              // Pass the image file to the onFileSelect callback
              console.log('Calling onFileSelect with the image file');
              onFileSelect(imageFile);
              console.log('PDF processing completed successfully');
              resolve(imageFile);
            } catch (blobError) {
              console.error('Error creating blob from data URL:', blobError);
              reject(blobError);
            }
          } catch (pdfError) {
            console.error('Error processing PDF document:', pdfError);
            reject(pdfError);
          }
        } catch (arrayBufferError) {
          console.error('Error processing ArrayBuffer:', arrayBufferError);
          reject(arrayBufferError);
        }
      };
      
      fileReader.onerror = function(error) {
        console.error('FileReader error:', error);
        reject(error);
      };
      
      console.log('Starting FileReader.readAsArrayBuffer');
      fileReader.readAsArrayBuffer(file);
      console.log('FileReader.readAsArrayBuffer called');
    });
  } catch (error) {
    console.error('Error in overall PDF processing:', error);
    throw error;
  }
};

/**
 * Validate file type, size, and process if valid
 * @param {File} file - The file to validate and process
 * @param {Function} onFileSelect - Callback function to call with the processed file
 * @param {Function} clearFileInput - Function to clear the file input
 */
export const validateAndProcessFile = (file, onFileSelect, clearFileInput) => {
  console.log('Validating file:', file.name, 'Type:', file.type, 'Size:', formatFileSize(file.size));
  const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const isPdf = file.type === 'application/pdf';
  
  // Check file type first
  if (!validImageTypes.includes(file.type) && !isPdf) {
    console.log('Invalid file type:', file.type);
    alert('Please upload a PNG, JPG, or PDF file.');
    clearFileInput();
    return;
  }
  
  // Check file size based on type
  if (isPdf && file.size > MAX_PDF_SIZE) {
    console.log('PDF file too large:', formatFileSize(file.size), 'max:', formatFileSize(MAX_PDF_SIZE));
    alert(`PDF file is too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(MAX_PDF_SIZE)}.`);
    clearFileInput();
    return;
  } else if (!isPdf && file.size > MAX_IMAGE_SIZE) {
    console.log('Image file too large:', formatFileSize(file.size), 'max:', formatFileSize(MAX_IMAGE_SIZE));
    alert(`Image file is too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(MAX_IMAGE_SIZE)}.`);
    clearFileInput();
    return;
  }
  
  // Process file based on type
  if (validImageTypes.includes(file.type)) {
    // For image files, pass directly to the callback
    console.log('Valid image file detected, passing to callback');
    onFileSelect(file);
  } else if (isPdf) {
    // For PDF files, process and extract the first page
    console.log('PDF file detected, starting processing');
    processPdfFile(file, onFileSelect, clearFileInput);
  }
};

/**
 * Processing wrapper for the component that shows loading indicators
 * @param {File} file - The PDF file to process
 * @param {Function} onFileSelect - Callback function to call with the processed file
 * @param {Function} clearFileInput - Function to clear the file input
 * @param {Function} setIsProcessingPdf - Function to set the processing state
 * @param {Function} setProcessingStatus - Function to set the processing status
 */
export const processPdfFileWithUI = async (file, onFileSelect, clearFileInput, setIsProcessingPdf, setProcessingStatus) => {
  setIsProcessingPdf(true);
  setProcessingStatus('Starting PDF processing');
  
  try {
    // Verify worker
    setProcessingStatus('Verifying PDF.js worker...');
    const workerAvailable = ensureWorkerIsAvailable();
    if (!workerAvailable) {
      console.error('PDF.js worker is not available');
      setProcessingStatus('Error: PDF.js worker not available');
      alert('PDF processing is not available at the moment. Please try again later or use an image file instead.');
      setIsProcessingPdf(false);
      clearFileInput();
      return;
    }
    
    console.log('PDF.js worker confirmed available, proceeding with processing');
    setProcessingStatus('PDF.js worker ready');
    
    // Create a callback wrapper to update UI
    const onProcessingComplete = (processedFile) => {
      onFileSelect(processedFile);
      setProcessingStatus('Complete');
      setIsProcessingPdf(false);
    };
    
    // Process the PDF with the UI-specific callback
    await processPdfFile(file, onProcessingComplete, clearFileInput);
  } catch (error) {
    console.error('Error in PDF processing:', error);
    setProcessingStatus('Error: ' + (error.message || 'Unknown error'));
    setIsProcessingPdf(false);
    clearFileInput();
  }
};

/**
 * Validate file type, size, and process if valid with UI feedback
 * @param {File} file - The file to validate and process
 * @param {Function} onFileSelect - Callback function to call with the processed file
 * @param {Function} clearFileInput - Function to clear the file input
 * @param {Function} setIsProcessingPdf - Function to set the processing state
 * @param {Function} setProcessingStatus - Function to set the processing status
 */
export const validateAndProcessFileWithUI = (file, onFileSelect, clearFileInput, setIsProcessingPdf, setProcessingStatus) => {
  console.log('Validating file with UI:', file.name, 'Type:', file.type, 'Size:', formatFileSize(file.size));
  const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const isPdf = file.type === 'application/pdf';
  
  // Check file type first
  if (!validImageTypes.includes(file.type) && !isPdf) {
    console.log('Invalid file type:', file.type);
    alert('Please upload a PNG, JPG, or PDF file.');
    clearFileInput();
    return;
  }
  
  // Check file size based on type
  if (isPdf && file.size > MAX_PDF_SIZE) {
    console.log('PDF file too large:', formatFileSize(file.size), 'max:', formatFileSize(MAX_PDF_SIZE));
    alert(`PDF file is too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(MAX_PDF_SIZE)}.`);
    clearFileInput();
    return;
  } else if (!isPdf && file.size > MAX_IMAGE_SIZE) {
    console.log('Image file too large:', formatFileSize(file.size), 'max:', formatFileSize(MAX_IMAGE_SIZE));
    alert(`Image file is too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(MAX_IMAGE_SIZE)}.`);
    clearFileInput();
    return;
  }
  
  // Process file based on type, but with UI handling for PDFs
  if (validImageTypes.includes(file.type)) {
    onFileSelect(file);
  } else if (isPdf) {
    processPdfFileWithUI(file, onFileSelect, clearFileInput, setIsProcessingPdf, setProcessingStatus);
  }
};

/**
 * DragDropFileUpload component that handles dragging and dropping files
 * with visual feedback and file validation (PNG/JPG/PDF)
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onFileSelect - Function to call when a file is selected
 * @param {string} props.fileName - Name of the currently selected file (if any)
 * @returns {JSX.Element} DragDropFileUpload component
 */
const DragDropFileUpload = ({ onFileSelect, fileName }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const fileInputRef = useRef(null);
  
  // Initialize the worker when the component mounts
  useEffect(() => {
    // Try to ensure the worker is loaded when the component mounts
    const initWorker = () => {
      try {
        console.log('Verifying PDF.js worker on component mount');
        const isAvailable = ensureWorkerIsAvailable();
        console.log('Worker availability check:', isAvailable);
      } catch (error) {
        console.error('Error checking worker availability:', error);
      }
    };
    
    initWorker();
  }, []);

  // Add a timeout for PDF processing to prevent it from getting stuck
  useEffect(() => {
    let timeoutId;
    
    if (isProcessingPdf) {
      console.log('Setting PDF processing timeout (30 seconds)');
      timeoutId = setTimeout(() => {
        console.error('PDF processing timed out after 30 seconds');
        setProcessingStatus('Error: Processing timed out. The PDF may be too large or complex.');
        setIsProcessingPdf(false);
        
        // Clear the input so the same file can be selected again if needed
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 30000); // 30 second timeout
    }
    
    // Clean up the timeout if component unmounts or processing completes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isProcessingPdf]);
  
  // Handle direct file input selection
  const handleFileInput = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndProcessFileWithUI(file, onFileSelect, clearFileInput, setIsProcessingPdf, setProcessingStatus);
    }
  };
  
  // Handle file drop
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      validateAndProcessFileWithUI(file, onFileSelect, clearFileInput, setIsProcessingPdf, setProcessingStatus);
    }
  };
  
  // Helper function to clear the file input
  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Handle drag over event
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  
  // Handle drag enter event
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };
  
  // Handle drag leave event
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };
  
  // Handle clicking on the upload area
  const handleClickUpload = () => {
    fileInputRef.current.click();
  };
  
  return (
    <div 
      className={`drag-drop-container ${isDragging ? 'dragging' : ''} ${isProcessingPdf ? 'processing' : ''}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClickUpload}
    >
      <input
        type="file"
        accept="image/png, image/jpeg, image/jpg, application/pdf"
        onChange={handleFileInput}
        ref={fileInputRef}
        className="file-input"
        id="file-upload"
      />
      
      {isProcessingPdf ? (
        <div className="processing-indicator">
          <div className="spinner"></div>
          <p>Processing PDF...</p>
          <p className="processing-status">{processingStatus}</p>
        </div>
      ) : (
        <>
          <div className="upload-icon">
            <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 16V8M12 8L8 12M12 8L16 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <p className="upload-text">
            {isDragging ? "Drop your file here" : "Drag & drop your floorplan file here"}
          </p>
          <p className="upload-text">or click to browse</p>
        </>
      )}
      
      {fileName && !isProcessingPdf && (
        <div className="file-name-container">
          <span className="file-name">{fileName}</span>
        </div>
      )}
      
    </div>
  );
};

export default DragDropFileUpload; 